import { useState, useEffect } from "react";
import { Sparkles, Plus, Pencil, Trash2, Check, X } from "lucide-react";

import {
  getAllPrompts,
  deletePrompt,
  addNewPrompt,
} from "../../../../api/chat";

import {
  ToolbarTriggerButton,
  DropdownPanel,
  DropdownEmptyState,
  smallIconBtnClasses,
} from "./_shared";

const MAX_PROMPTS = 10;

const DropDownPromptLoader = (props) => {
  const [promptList, setPromptList] = useState([]);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const canUserAddPrompts = promptList.length < MAX_PROMPTS;

  const refreshPromptList = () => {
    getAllPrompts().then((response) => {
      const sortedList = (response?.data || [])
        .map((item) => item.prompt)
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      setPromptList(sortedList);
    });
  };

  useEffect(() => {
    if (props.isPromptLoaderOpen) refreshPromptList();
  }, [props.isPromptLoaderOpen]);

  const addPromptToList = (prompt) =>
    addNewPrompt(prompt).then(() => refreshPromptList());

  const deletePromptFromList = (prompt) =>
    deletePrompt(prompt).then(() => refreshPromptList());

  const editPromptFromList = async (oldPrompt, newPrompt) => {
    await deletePrompt(oldPrompt);
    await addNewPrompt(newPrompt);
    refreshPromptList();
  };

  const trimmedEntry = props.chatEntry?.trim();
  const canAdd =
    canUserAddPrompts &&
    trimmedEntry !== "" &&
    !promptList.includes(trimmedEntry);

  const handleToggleOpen = () => {
    props.setIsPromptLoaderOpen(!props.isPromptLoaderOpen);
    props.setIsAgentSelectorOpen?.(false);
    props.setIsDropDownNotesOpen(false);
  };

  return (
    <div className="relative">
      <ToolbarTriggerButton
        icon={Sparkles}
        label="Prompts"
        onClick={handleToggleOpen}
        active={props.isPromptLoaderOpen}
      />

      <DropdownPanel isOpen={props.isPromptLoaderOpen}>
        <div className="p-2">
          {!canUserAddPrompts && (
            <div className="mb-2 rounded-lg px-3 py-2 text-[12px] font-normal tracking-wide bg-inputBg/40 text-mutedText">
              You are using all {MAX_PROMPTS} of your available prompts.
            </div>
          )}

          {canAdd && (
            <button
              type="button"
              className="group mb-1 w-full flex items-center gap-2 rounded-lg border border-dashed border-inputBorder
                         px-3 py-2 text-left text-[13px] font-normal tracking-wide text-mutedText
                         hover:border-velBlue/40 hover:text-mainText transition-colors cursor-pointer
                         outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
              onClick={(e) => {
                e.stopPropagation();
                addPromptToList(trimmedEntry);
              }}
            >
              <Plus className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={2} />
              <span className="truncate">{trimmedEntry}</span>
            </button>
          )}

          {promptList.length > 0 ? (
            <ul className="space-y-0.5">
              {promptList.map((prompt) =>
                editingPrompt === prompt ? (
                  <li key={prompt}>
                    <div className="flex items-center gap-2 rounded-lg px-3 py-2 bg-inputBg/40">
                      <input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className="block w-full rounded-lg px-3 py-1.5 text-[13px] font-normal tracking-wide
                                   bg-transparent border border-inputBorder text-mainText
                                   placeholder:text-mutedText
                                   focus:outline-none focus:ring-2 focus:ring-inputFocus/60"
                        placeholder="Edit prompt"
                        autoFocus
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingValue.trim()) {
                            editPromptFromList(prompt, editingValue.trim());
                          }
                          setEditingPrompt(null);
                        }}
                        className={smallIconBtnClasses}
                        aria-label="Save"
                      >
                        <Check
                          className="h-3.5 w-3.5 text-activeCase"
                          strokeWidth={2}
                        />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingPrompt(null);
                        }}
                        className={smallIconBtnClasses}
                        aria-label="Cancel"
                      >
                        <X
                          className="h-3 w-3 text-logoutColor"
                          strokeWidth={2}
                        />
                      </button>
                    </div>
                  </li>
                ) : (
                  <li key={prompt}>
                    {/* NOTE: nested <span onClick> inside <button> is
                        invalid HTML; preserved from original. Flag for
                        follow-up pass. */}
                    <button
                      type="button"
                      onClick={() => {
                        props.setChatEntry?.(prompt);
                        props.setIsPromptLoaderOpen(false);
                      }}
                      className="group flex w-full items-center justify-between rounded-lg px-3 py-2
                                 text-left transition-colors cursor-pointer hover:bg-btnHover
                                 outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                    >
                      <p className="truncate pr-3 text-[13px] font-normal tracking-wide text-mainText">
                        {prompt}
                      </p>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span
                          className={smallIconBtnClasses}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingPrompt(prompt);
                            setEditingValue(prompt);
                          }}
                          aria-label="Edit"
                          role="button"
                        >
                          <Pencil className="h-3 w-3" strokeWidth={2} />
                        </span>
                        <span
                          className={smallIconBtnClasses}
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePromptFromList(prompt);
                          }}
                          aria-label="Delete"
                          role="button"
                        >
                          <Trash2
                            className="h-3 w-3 text-logoutColor"
                            strokeWidth={2}
                          />
                        </span>
                      </div>
                    </button>
                  </li>
                ),
              )}
            </ul>
          ) : (
            <DropdownEmptyState>No saved prompts yet.</DropdownEmptyState>
          )}
        </div>
      </DropdownPanel>
    </div>
  );
};

export default DropDownPromptLoader;
